import { InView } from 'react-intersection-observer'
import { useMemo } from 'react'
import FlexibleLink from 'src/components/ui/FlexibleLink/FlexibleLink'
import { Picture } from 'src/components/ui/Image'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'

interface CampaignBannerProps {
  section?: string
  locationOnPage?: string
  showOnDesktop?: boolean
  showOnMobile?: boolean
  imageDesktop?: string
  imageMobile?: string
  link?: string
  altText?: string
}

export default function CampaignBanner({
  section,
  locationOnPage,
  showOnDesktop,
  showOnMobile,
  imageDesktop,
  imageMobile,
  link,
  altText,
}: Readonly<CampaignBannerProps>) {
  const { pageData } = useGlobalContext()

  const desktop = imageDesktop ?? ''
  const mobile = imageMobile ?? ''

  const trimmedAlt = altText?.trim() ?? ''
  const fallbackAlt = trimmedAlt.length > 0 ? trimmedAlt : 'Banner de Campanha'
  const imageAlt = link ? fallbackAlt : trimmedAlt

  const isTopOfPage = locationOnPage === 'top'

  const visibilityClasses = useMemo(() => {
    return getResponsiveVisibility(showOnDesktop, showOnMobile)
  }, [showOnDesktop, showOnMobile])

  const buildEventData = () => ({
    ...getStandardObject(pageData),
    ...getFeatureObject({
      section: section ?? '',
      locationOnPage: locationOnPage ?? null,
      name: fallbackAlt,
    }),
  })

  const sendPromoFeatureViewedEvent = () => {
    if (!section || !locationOnPage) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Promo Feature Viewed',
      eventData: buildEventData(),
    })
  }

  const sendPromotionalElementSelected = () => {
    if (!section || !locationOnPage) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: buildEventData(),
    })
  }

  if (!desktop && !mobile) {
    return null
  }

  const image = (
    <Picture
      className="block h-full w-full object-cover"
      sources={[
        {
          src: mobile || desktop,
          width: 390,
          height: 475,
          media: '(max-width: 767px)',
        },
        {
          src: desktop || mobile,
          width: 1440,
          height: 475,
          media: '(min-width: 768px)',
        },
      ]}
      img={{
        src: desktop || mobile,
        width: 1440,
        height: 475,
        'data-id': desktop || mobile,
        alt: imageAlt,
        loading: isTopOfPage ? 'eager' : 'lazy',
        fetchpriority: isTopOfPage ? 'high' : 'auto',
      }}
      shouldCompress={false}
    />
  )

  return (
    <InView
      as="section"
      threshold={0.7}
      triggerOnce
      className={`block w-full aspect-[390/475] max-h-[480px] md:max-h-[462px] md:aspect-[1440/475] overflow-hidden ${visibilityClasses}`}
      data-section={section}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
    >
      {link ? (
        <FlexibleLink
          linkUrl={link}
          classNames="block h-full w-full outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#101010]"
          onClick={sendPromotionalElementSelected}
        >
          {image}
        </FlexibleLink>
      ) : (
        image
      )}
    </InView>
  )
}
